import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { collection, query, where, getDocs, doc, getDoc, updateDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../index";
import Spinner from "../spinner";
import Cookies from 'js-cookie';
import { HDate } from "hebcal";
import globalMasehtot from "../SiumShas/masehtot.json";

const SiumimManager = () => {
    const { siumId } = useParams();
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [siumim, setSiumim] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isInitialLoading, setIsInitialLoading] = useState(true);
    const [isSearched, setIsSearched] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [currentSium, setCurrentSium] = useState(null);
    const [siumNotFound, setSiumNotFound] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [siumToDelete, setSiumToDelete] = useState(null);
    const [showColorPicker, setShowColorPicker] = useState(null);
    const [showNewSiumModal, setShowNewSiumModal] = useState(false);
    const [progressData, setProgressData] = useState({}); // { siumId: { takenPercent, learnedPercent } }
    const navigate = useNavigate();

    // Available background colors for sium cards
    const availableColors = [
        '#FFE5E5', // Light pink
        '#E5F3FF', // Light blue
        '#FFF4E5', // Light orange
        '#E5FFE5', // Light green
        '#F3E5FF', // Light purple
        '#FFFFE5', // Light yellow
        '#E5F9FF', // Light cyan
        '#FFE5F9', // Light rose
        '#F0F0F0', // Light gray
        '#E5FFFA', // Light mint
    ];

    // Get siumId from URL or cookies
    const getActiveSiumId = () => {
        if (siumId) return siumId;
        return Cookies.get('siumShasId') || null;
    };

    // Fetch sium from both collections
    const fetchSiumFromBothCollections = async (id) => {
        // Try siumim collection first
        try {
            const siumRef = doc(db, "siumim", id);
            const siumSnap = await getDoc(siumRef);
            if (siumSnap.exists()) {
                return { data: { id: siumSnap.id, ...siumSnap.data() }, type: 'siumim' };
            }
        } catch (error) {
            console.error("Error fetching from siumim:", error);
        }

        // Try siumimAher collection
        try {
            const siumAherRef = doc(db, "siumimAher", id);
            const siumAherSnap = await getDoc(siumAherRef);
            if (siumAherSnap.exists()) {
                return { data: { id: siumAherSnap.id, ...siumAherSnap.data() }, type: 'siumimAher' };
            }
        } catch (error) {
            console.error("Error fetching from siumimAher:", error);
        }

        return null;
    };

    // Initial load - check for siumId from URL or cookies
    useEffect(() => {
        const initializeManager = async () => {
            setIsInitialLoading(true);
            const activeSiumId = getActiveSiumId();

            if (activeSiumId) {
                const result = await fetchSiumFromBothCollections(activeSiumId);
                if (result) {
                    setCurrentSium(result.data);

                    // Set email and phone from the sium
                    const siumEmail = result.data.organizerEmail || '';
                    const siumPhone = Array.isArray(result.data.organizerPhone)
                        ? result.data.organizerPhone[0]
                        : result.data.organizerPhone || '';

                    setEmail(siumEmail);
                    setPhone(siumPhone);

                    // Search for all siumim with this email/phone
                    await searchSiumim(siumEmail, siumPhone, activeSiumId);
                } else {
                    setSiumNotFound(true);
                }
            }

            setIsInitialLoading(false);
        };

        initializeManager();
    }, [siumId]);

    // Close color picker when clicking outside
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (showColorPicker && !e.target.closest('.sium-card-color-picker')) {
                setShowColorPicker(null);
            }
        };

        if (showColorPicker) {
            document.addEventListener('click', handleClickOutside);
            return () => document.removeEventListener('click', handleClickOutside);
        }
    }, [showColorPicker]);

    // Fetch progress data for all siumim
    useEffect(() => {
        if (siumim.length === 0) return;

        const fetchProgress = async () => {
            const progress = {};

            await Promise.all(siumim.map(async (sium) => {
                try {
                    if (sium.collectionType === 'siumimAher') {
                        // For siumimAher: query usersAher to get pieces data
                        const q = query(collection(db, "usersAher"), where("siumId", "==", sium.id));
                        const snapshot = await getDocs(q);
                        let totalPieces = 0;
                        let takenPieces = 0;
                        let learnedPieces = 0;

                        snapshot.forEach((userDoc) => {
                            const userData = userDoc.data();
                            const pieces = userData.pieces || {};
                            const count = Object.keys(pieces).length;
                            const learned = Object.values(pieces).filter(v => v === true).length;
                            totalPieces += count;
                            takenPieces += count;
                            learnedPieces += learned;
                        });

                        progress[sium.id] = {
                            takenPercent: totalPieces > 0 ? Math.floor((takenPieces / (sium.totalPieces || totalPieces)) * 100) : 0,
                            learnedPercent: totalPieces > 0 ? Math.floor((learnedPieces / (sium.totalPieces || totalPieces)) * 100) : 0
                        };
                    } else {
                        // For siumim (shas): query users to get pages/masehtot data
                        const q = query(collection(db, "users"), where("siumId", "==", sium.id));
                        const snapshot = await getDocs(q);
                        let takenCount = 0;
                        let learnedCount = 0;

                        if (sium.haluka === 'dapim') {
                            // Dapim mode: users have `pages` object { "masehetKey-pageNum": true/false }
                            snapshot.forEach((userDoc) => {
                                const userData = userDoc.data();
                                const pages = userData.pages || {};
                                takenCount += Object.keys(pages).length;
                                learnedCount += Object.values(pages).filter(v => v === true).length;
                                // Log masehet:learner mapping
                                const masehtotFromPages = [...new Set(Object.keys(pages).map(k => k.split('-')[0]))];
                                masehtotFromPages.forEach(m => {
                                    console.log({[m]: userData.fullName});
                                });
                            });

                            const total = sium.pagesCounter || takenCount || 1;
                            progress[sium.id] = {
                                takenPercent: Math.min(Math.floor((takenCount / total) * 100), 100),
                                learnedPercent: Math.min(Math.floor((learnedCount / total) * 100), 100)
                            };
                        } else {
                            // Masehtot mode: replicate exact logic from siumMasehtotStatus.js
                            let pagesWereTaken = 0;
                            let pagesWereLeanred = 0;

                            // Build siumStatus from masehtotSelection (same as siumMasehtotStatus)
                            let siumStatus = {};
                            for (const masehetKey of Object.keys(sium.masehtotSelection)) {
                                let chapterIndex = 0;
                                siumStatus[masehetKey] = {};
                                siumStatus[masehetKey]["chapters"] = [];
                                for (const chapter of globalMasehtot.allMasehtot[masehetKey].chapters) {
                                    if (globalMasehtot.allMasehtot[masehetKey].isMishna) {
                                        siumStatus[masehetKey]["isMishna"] = true;
                                        siumStatus[masehetKey]["chapters"].push({
                                            wasTaken: sium.masehtotSelection[masehetKey][chapterIndex],
                                        });
                                    } else {
                                        const startIndex = globalMasehtot.onlyPages.indexOf(chapter.startPage.replace(/[.:]/g, ""));
                                        const endIndex = globalMasehtot.onlyPages.indexOf(chapter.endPage.replace(/[.:]/g, ""));
                                        const pageCount = endIndex - startIndex + 1;
                                        siumStatus[masehetKey]["chapters"].push({
                                            pageCount: pageCount,
                                            wasTaken: sium.masehtotSelection[masehetKey][chapterIndex]
                                        });
                                    }
                                    chapterIndex++;
                                }
                            }

                            // Process users (same logic as siumMasehtotStatus)
                            snapshot.forEach((userDoc) => {
                                const userData = userDoc.data();
                                const masehtot = userData.masehtot || [];
                                for (const masehet of masehtot) {
                                    const masehetKey = masehet.masehetId;
                                    if (!siumStatus[masehetKey]) continue;
                                    // Log masehet:learner mapping
                                    console.log({[masehetKey]: userData.fullName});
                                    for (const chapter of masehet.chapters) {
                                        const chapterIndex = parseInt(chapter.chapterNum) - 1;
                                        const properChapter = siumStatus[masehetKey]["chapters"][chapterIndex];
                                        if (!properChapter) continue;

                                        if (siumStatus[masehetKey]["isMishna"]) {
                                            const chapterWasLearned = chapter.finish.every((page) => page);
                                            if (properChapter.user) {
                                                if (!properChapter["wasLearned"] && chapterWasLearned) {
                                                    pagesWereLeanred += 1;
                                                    properChapter["wasLearned"] = chapterWasLearned;
                                                }
                                            } else {
                                                pagesWereTaken += 1;
                                                if (chapterWasLearned) {
                                                    pagesWereLeanred += 1;
                                                }
                                            }
                                        } else {
                                            const partialLearned = properChapter["user"] && (properChapter["partialLearned"] || false);
                                            const chapterPagesLearned = chapter.finish.filter((page) => page).length;
                                            const chapterWasLearned = chapterPagesLearned === chapter.finish.length;
                                            if (properChapter.user) {
                                                if (!partialLearned && chapterPagesLearned > 0) {
                                                    pagesWereLeanred += chapterPagesLearned;
                                                    properChapter["partialLearned"] = true;
                                                    properChapter["wasLearned"] = chapterWasLearned;
                                                }
                                            } else {
                                                pagesWereTaken += chapter.finish.length;
                                                pagesWereLeanred += chapterPagesLearned;
                                                if (chapterPagesLearned > 0) {
                                                    properChapter["partialLearned"] = true;
                                                }
                                            }
                                        }

                                        if (!properChapter.user) {
                                            properChapter["user"] = userDoc.id;
                                        }
                                    }
                                }
                            });

                            const total = sium.pagesCounter || 1;
                            progress[sium.id] = {
                                takenPercent: Math.floor((pagesWereTaken / total) * 100),
                                learnedPercent: Math.floor((pagesWereLeanred / total) * 100)
                            };
                        }
                    }
                } catch (error) {
                    console.error("Error fetching progress for sium:", sium.id, error);
                    progress[sium.id] = { takenPercent: 0, learnedPercent: 0 };
                }
            }));

            setProgressData(progress);
        };

        fetchProgress();
    }, [siumim]);

    const handlePhoneChange = (e) => {
        const inputValue = e.target.value;
        const numericValue = inputValue.replace(/\D/g, '');
        setPhone(numericValue);
    };

    // Sort siumim: current first, then by lastAccessed (most recent first)
    const sortSiumim = (siumimList, currentId) => {
        return siumimList.sort((a, b) => {
            // Current sium always first
            if (a.id === currentId) return -1;
            if (b.id === currentId) return 1;

            const aLastAccessed = a.lastAccessed?.toDate ? a.lastAccessed.toDate() : (a.lastAccessed ? new Date(a.lastAccessed) : null);
            const bLastAccessed = b.lastAccessed?.toDate ? b.lastAccessed.toDate() : (b.lastAccessed ? new Date(b.lastAccessed) : null);

            // Sort by lastAccessed descending (most recent first)
            if (aLastAccessed && bLastAccessed) return bLastAccessed - aLastAccessed;
            if (aLastAccessed && !bLastAccessed) return -1;
            if (!aLastAccessed && bLastAccessed) return 1;

            return 0;
        });
    };

    const searchSiumim = async (searchEmail, searchPhone, currentId = null) => {
        try {
            let allSiumim = [];

            // Both email and phone are required for AND logic
            if (!searchEmail?.trim() || !searchPhone?.trim()) {
                setSiumim([]);
                setIsSearched(true);
                setErrorMessage('יש להזין גם אימייל וגם טלפון');
                return;
            }

            const normalizedPhone = searchPhone.trim().replace(/\D/g, '');

            // Search in siumim collection
            const siumimRef = collection(db, "siumim");
            const emailQuerySiumim = query(siumimRef, where("organizerEmail", "==", searchEmail.trim()));
            const siumimSnapshot = await getDocs(emailQuerySiumim);

            siumimSnapshot.forEach((doc) => {
                const data = doc.data();
                const docPhone = Array.isArray(data.organizerPhone)
                    ? data.organizerPhone[0]
                    : data.organizerPhone || '';
                const normalizedDocPhone = docPhone.replace(/\D/g, '');

                if (normalizedDocPhone === normalizedPhone) {
                    allSiumim.push({ id: doc.id, ...data, collectionType: 'siumim' });
                }
            });

            // Search in siumimAher collection
            const siumimAherRef = collection(db, "siumimAher");
            const emailQuerySiumimAher = query(siumimAherRef, where("organizerEmail", "==", searchEmail.trim()));
            const siumimAherSnapshot = await getDocs(emailQuerySiumimAher);

            siumimAherSnapshot.forEach((doc) => {
                const data = doc.data();
                const docPhone = Array.isArray(data.organizerPhone)
                    ? data.organizerPhone[0]
                    : data.organizerPhone || '';
                const normalizedDocPhone = docPhone.replace(/\D/g, '');

                if (normalizedDocPhone === normalizedPhone) {
                    allSiumim.push({ id: doc.id, ...data, collectionType: 'siumimAher' });
                }
            });

            // Sort: current first, then active, then finished
            const activeSiumId = currentId || getActiveSiumId();

            // Filter out deleted siums
            allSiumim = allSiumim.filter(sium => !sium.deleted);

            allSiumim = sortSiumim(allSiumim, activeSiumId);

            setSiumim(allSiumim);
            setIsSearched(true);

            if (allSiumim.length === 0) {
                setErrorMessage('לא נמצאו סיומים עבור הפרטים שהוזנו');
            }
        } catch (error) {
            console.error("Error fetching siumim:", error);
            setErrorMessage('אירעה שגיאה בחיפוש');
        }
    };

    // Handle soft delete of a sium
    const handleDeleteSium = async () => {
        if (!siumToDelete) return;

        try {
            const collectionName = siumToDelete.collectionType === 'siumimAher' ? 'siumimAher' : 'siumim';
            const siumRef = doc(db, collectionName, siumToDelete.id);
            await updateDoc(siumRef, { deleted: true });

            // Remove from local state
            setSiumim(prev => prev.filter(s => s.id !== siumToDelete.id));
            setShowDeleteModal(false);
            setSiumToDelete(null);
        } catch (error) {
            console.error("Error deleting sium:", error);
            alert('שגיאה במחיקת הסיום');
        }
    };

    // Check if sium has ended
    const isSiumEnded = (sium) => {
        const now = new Date();
        const endDate = sium.endSium?.toDate ? sium.endSium.toDate() : (sium.endSium ? new Date(sium.endSium) : null);
        return endDate && endDate < now;
    };

    const handleSearch = async (e) => {
        e.preventDefault();

        if (!email.trim() || !phone.trim()) {
            setErrorMessage('יש להזין אימייל ופלאפון');
            return;
        }

        setIsLoading(true);
        setErrorMessage('');
        setSiumim([]);

        await searchSiumim(email, phone);

        setIsLoading(false);
    };

    // Hebrew letters for numbers
    const hebrewNumerals = {
        1: 'א', 2: 'ב', 3: 'ג', 4: 'ד', 5: 'ה', 6: 'ו', 7: 'ז', 8: 'ח', 9: 'ט',
        10: 'י', 11: 'י"א', 12: 'י"ב', 13: 'י"ג', 14: 'י"ד', 15: 'ט"ו', 16: 'ט"ז',
        17: 'י"ז', 18: 'י"ח', 19: 'י"ט', 20: 'כ', 21: 'כ"א', 22: 'כ"ב', 23: 'כ"ג',
        24: 'כ"ד', 25: 'כ"ה', 26: 'כ"ו', 27: 'כ"ז', 28: 'כ"ח', 29: 'כ"ט', 30: 'ל'
    };

    // Convert number to Hebrew letters for year
    const numberToHebrewYear = (year) => {
        const shortYear = year % 1000;
        const hundreds = Math.floor(shortYear / 100);
        const tens = Math.floor((shortYear % 100) / 10);
        const ones = shortYear % 10;

        const hundredsLetters = ['', 'ק', 'ר', 'ש', 'ת', 'תק', 'תר', 'תש', 'תת', 'תתק'];
        const tensLetters = ['', 'י', 'כ', 'ל', 'מ', 'נ', 'ס', 'ע', 'פ', 'צ'];
        const onesLetters = ['', 'א', 'ב', 'ג', 'ד', 'ה', 'ו', 'ז', 'ח', 'ט'];

        if (tens === 1 && ones === 5) return hundredsLetters[hundreds] + 'ט"ו';
        if (tens === 1 && ones === 6) return hundredsLetters[hundreds] + 'ט"ז';

        let result = hundredsLetters[hundreds] + tensLetters[tens] + onesLetters[ones];

        if (result.length > 1) {
            result = result.slice(0, -1) + '"' + result.slice(-1);
        } else if (result.length === 1) {
            result = result + "'";
        }

        return result;
    };

    const formatDate = (timestamp) => {
        if (!timestamp) return '-';
        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);

        try {
            const hDate = new HDate(date);
            const day = hDate.getDate();
            const monthName = hDate.getMonthName('h');
            const year = hDate.getFullYear();

            const hebrewDay = hebrewNumerals[day] || day;
            const hebrewYear = numberToHebrewYear(year);

            return `${hebrewDay} ${monthName} ${hebrewYear}`;
        } catch (error) {
            return date.toLocaleDateString('he-IL');
        }
    };

    const getSiumStatus = (sium) => {
        const now = new Date();
        const endDate = sium.endSium?.toDate ? sium.endSium.toDate() : (sium.endSium ? new Date(sium.endSium) : null);

        if (endDate && endDate < now) {
            return { text: 'הסתיים', color: '#dc3545' };
        }

        return { text: 'פעיל', color: '#5DC9E0' };
    };

    const navigateToSium = async (sium) => {
        // Update lastAccessed timestamp
        try {
            const collectionName = sium.collectionType === 'siumimAher' ? 'siumimAher' : 'siumim';
            const siumRef = doc(db, collectionName, sium.id);
            await updateDoc(siumRef, { lastAccessed: serverTimestamp() });
        } catch (error) {
            console.error("Error updating lastAccessed:", error);
        }

        if (sium.collectionType === 'siumimAher') {
            navigate(`/niulSiumAher/${sium.id}/siumStatus`);
        } else {
            navigate(`/niulSium/${sium.id}/siumStatus`);
        }
    };

    const getSiumTypeLabel = (sium) => {
        if (sium.collectionType === 'siumimAher') {
            return sium.bookName || 'ספר אחר';
        }
        return sium.haluka === 'dapim' ? "ש\"ס לפי דפים" : "ש\"ס לפי מסכתות";
    };

    // Get or assign background color for sium
    const getSiumBackgroundColor = (sium, allSiumim) => {
        // If sium already has a color, return it
        if (sium.backgroundColor) {
            return sium.backgroundColor;
        }

        // Get all colors already used by other siums
        const usedColors = allSiumim
            .filter(s => s.id !== sium.id && s.backgroundColor)
            .map(s => s.backgroundColor);

        // Find first available color
        const availableColor = availableColors.find(color => !usedColors.includes(color));

        // If found an available color, save it
        if (availableColor) {
            saveBackgroundColor(sium, availableColor);
            return availableColor;
        }

        // If all colors are used, use the first one
        const defaultColor = availableColors[0];
        saveBackgroundColor(sium, defaultColor);
        return defaultColor;
    };

    // Save background color to database
    const saveBackgroundColor = async (sium, color) => {
        try {
            const collectionName = sium.collectionType === 'siumimAher' ? 'siumimAher' : 'siumim';
            const siumRef = doc(db, collectionName, sium.id);
            await updateDoc(siumRef, { backgroundColor: color });

            // Update local state
            setSiumim(prev => prev.map(s =>
                s.id === sium.id ? { ...s, backgroundColor: color } : s
            ));
        } catch (error) {
            console.error("Error saving background color:", error);
        }
    };

    // Handle color change
    const handleColorChange = (sium, newColor) => {
        saveBackgroundColor(sium, newColor);
        setShowColorPicker(null);
    };

    const activeSiumId = getActiveSiumId();

    if (isInitialLoading) {
        return <Spinner />;
    }

    return (
        <div className="siumim-manager-container">
            <div className="logo-icon" onClick={() => navigate("/")}>
                <svg width="135" height="49" viewBox="0 0 135 49" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M86.3894 46.9941H85.3384L87.4833 41.3423H84.0193V39.4869H84.9845V40.5594H88.7702L86.3894 46.9941Z" fill="#1F2B70"/>
                    <path d="M82.7686 40.5594V44.2057H81.7605V40.5594H82.7686Z" fill="#1F2B70"/>
                    <path d="M74.643 40.5594H77.5172C79.351 40.5594 80.1017 41.353 80.1017 43.5944V46.9941H77.1096V46.2112H79.0936V43.6159C79.0936 41.9 78.6218 41.3423 77.3456 41.3423H76.6056L75.6189 46.9941H74.6001L75.6189 41.3423H74.643V40.5594Z" fill="#1F2B70"/>
                    <path d="M73.0038 40.5594V46.9941H71.9957V40.5594H73.0038Z" fill="#1F2B70"/>
                    <path d="M65.4352 40.5594H70.465V41.3423H69.6499V46.9941H68.6419V41.3423H65.4352V40.5594Z" fill="#1F2B70"/>
                    <path d="M55.5901 40.5594H58.4643C60.2982 40.5594 61.0489 41.353 61.0489 43.5944V46.9941H58.0568V46.2112H60.0408V43.6159C60.0408 41.9 59.5689 41.3423 58.2927 41.3423H57.5527L56.5661 46.9941H55.5472L56.5661 41.3423H55.5901V40.5594Z" fill="#1F2B70"/>
                    <path d="M48.8315 40.5594H51.8987C53.5502 40.5594 54.2259 41.16 54.2259 43.3049V46.9941H53.2178V43.3156C53.2178 41.7284 52.7888 41.3423 51.6306 41.3423H49.8396V46.9941H48.8315V40.5594Z" fill="#1F2B70"/>
                    <path d="M46.7955 40.5594V46.9941H45.7874V40.5594H46.7955Z" fill="#1F2B70"/>
                    <path d="M42.0379 46.9941H40.9869L43.1318 41.3423H39.6678V39.4869H40.633V40.5594H44.4187L42.0379 46.9941Z" fill="#1F2B70"/>
                    <path d="M33.0906 40.5594H38.4635V44.3023C38.4635 46.2863 37.7343 46.9941 36.0612 46.9941H35.3856V46.2112H35.9111C36.9621 46.2112 37.4662 45.8144 37.4662 44.313V41.3423H33.0906V40.5594ZM33.1549 48.6028V43.2298H34.163V48.6028H33.1549Z" fill="#1F2B70"/>
                    <path d="M27.9071 40.5594V46.2112H28.7758V46.9941H23.3707V46.2112H26.899V41.3423H23.3707V40.5594H27.9071Z" fill="#1F2B70"/>
                    <path d="M16.5361 40.5594H21.909V44.3023C21.909 46.2863 21.1798 46.9941 19.5067 46.9941H18.8311V46.2112H19.3566C20.4076 46.2112 20.9116 45.8144 20.9116 44.313V41.3423H16.5361V40.5594ZM16.6004 48.6028V43.2298H17.6085V48.6028H16.6004Z" fill="#1F2B70"/>
                    <path d="M12.9795 46.9941H11.9285L14.0734 41.3423H10.6094V39.4869H11.5746V40.5594H15.3604L12.9795 46.9941Z" fill="#1F2B70"/>
                    <path d="M9.25981 40.5594V46.9941H8.25171V40.5594H9.25981Z" fill="#1F2B70"/>
                    <path d="M0.484871 40.5594H3.93815C5.58971 40.5594 6.35115 41.2351 6.35115 43.3049V46.9941H5.34305V43.3907C5.34305 41.8142 4.82827 41.3423 3.72366 41.3423H2.40455V46.9941H0.120239V46.2112H1.43935V41.3423H0.484871V40.5594Z" fill="#1F2B70"/>
                    <path d="M78.8004 23.4675C78.2379 23.4675 77.5854 23.1975 77.5854 22.365V19.575C77.5854 19.1025 77.9679 18.8325 78.2829 18.8325C78.3054 19.3275 78.7554 19.575 79.2054 19.575H85.7304C87.6879 19.575 88.6104 20.835 88.6104 22.6575V28.98L87.0354 32.6925L79.1154 32.6475C78.6429 31.5675 77.8554 29.565 77.8779 27.36C77.9004 25.6275 78.8904 24.5475 80.1504 23.4675H78.8004ZM79.9254 28.755H88.4529L88.2954 25.92C88.2054 24.255 87.4629 23.4675 85.8429 23.4675H80.3979C79.0029 24.66 79.0479 26.145 79.9254 28.755Z" fill="#1F2B70"/>
                    <path d="M75.749 20.6325C75.9515 21.7575 75.929 24.1425 74.399 27.2925L74.219 27.2025C75.2315 25.0425 75.1865 23.4675 73.769 23.4675H71.519C70.934 23.4675 70.3265 23.1975 70.3265 22.365V19.575C70.3265 19.1025 70.709 18.8325 70.979 18.8325C71.0465 19.35 71.384 19.575 71.9465 19.575H74.2865C75.1865 19.575 75.614 19.9125 75.749 20.6325Z" fill="#1F2B70"/>
                    <path d="M68.2405 24.165C68.2855 23.7825 68.218 23.4675 67.813 23.4675H64.7755C64.213 23.4675 63.5605 23.1975 63.5605 22.365V19.575C63.5605 19.1025 63.988 18.8325 64.258 18.8325C64.2805 19.35 64.708 19.575 65.1805 19.575H67.5205C68.5555 19.575 68.713 19.9125 68.668 21.06L68.2855 25.5825C68.1505 27.0675 69.343 29.0925 69.163 30.9375C69.118 31.7025 68.938 32.7375 67.408 32.7375L68.2405 24.165Z" fill="#1F2B70"/>
                    <path d="M58.985 19.575C60.92 19.575 61.91 20.79 61.91 22.59V28.89L61.595 32.6475H51.5375V27.6075C51.5375 25.875 53.045 24.3 53.765 23.4675H52.1675C51.65 23.4675 50.9975 23.1975 50.9975 22.365V19.575C50.9975 19.1025 51.4025 18.81 51.65 18.8325C51.695 19.35 52.1225 19.575 52.595 19.575H58.985ZM53.2025 28.755H61.7075L61.595 25.8525C61.5275 24.1875 60.83 23.4675 59.21 23.4675H53.99C53.27 24.2325 53.2025 24.5025 53.2025 25.5375V28.755Z" fill="#1F2B70"/>
                    <path d="M35.6514 23.4675C35.0664 23.4675 34.4139 23.1975 34.4139 22.365V19.575C34.4139 19.1025 34.8414 18.8325 35.1339 18.8325C35.1339 19.3725 35.6064 19.575 36.0789 19.575H43.6614C44.5839 19.575 44.7414 20.0025 44.7414 20.7V23.4675H43.3464V25.8525C43.3464 27.36 44.7864 29.0925 44.7864 30.9375C44.7864 31.7025 44.2239 32.715 43.1664 32.7375V23.4675H35.6514ZM34.5039 32.7375V29.5425C34.5039 27.72 35.1339 26.64 35.6964 26.2575C35.9664 26.0775 36.2364 26.0775 36.3714 26.1225C36.1914 26.4825 35.9664 27.2025 35.9664 27.8775C35.9664 28.89 36.2589 29.97 36.2589 31.3875C36.2589 32.2875 35.7639 32.715 34.5039 32.7375Z" fill="#1F2B70"/>
                    <path d="M24.8145 23.4675C24.297 23.4675 23.6445 23.1975 23.6445 22.365V19.575C23.6445 19.1025 24.0045 18.8325 24.297 18.8325C24.342 19.2825 24.792 19.575 25.2645 19.575H26.7945C27.717 19.575 27.897 20.0925 27.897 20.5425V23.4675H26.547L22.632 28.755H27.582C29.877 28.755 30.192 28.17 30.9795 26.8425C32.2845 24.6375 32.5545 24.4575 32.5545 23.4675H29.6745C29.157 23.4675 28.5045 23.1975 28.5045 22.365V19.575C28.5045 19.1025 28.8645 18.8325 29.157 18.8325C29.202 19.2825 29.652 19.575 30.1245 19.575H31.0695C32.217 19.575 32.712 20.0475 32.712 21.015V23.58C32.712 25.605 31.002 25.5375 31.002 30.105C31.002 32.1975 29.922 32.6475 28.0095 32.6475H21.147V30.8925C21.147 29.6325 19.572 27.2025 19.572 25.9425C19.572 24.975 20.0445 23.85 21.147 23.4675H19.9095C19.347 23.4675 18.717 23.1975 18.717 22.365V19.575C18.717 19.1025 19.122 18.8325 19.392 18.8325C19.4145 19.2825 19.8645 19.575 20.337 19.575H21.9345C22.812 19.575 23.037 20.0925 23.037 20.5425V23.4675H21.9345C21.372 23.4675 20.1345 23.8275 20.1345 25.1325C20.1345 26.64 22.137 27.4725 22.4295 28.7325L26.322 23.4675H24.8145Z" fill="#1F2B70"/>
                    <path d="M13.6264 18C14.4139 18 14.6389 18.3375 14.7289 18.8775C14.8864 19.845 14.6614 21.96 12.4339 23.1975L12.2764 23.04C12.8164 21.9825 12.9064 20.1825 12.4789 18.1125C12.8614 18.0225 13.2439 18 13.6264 18ZM14.9089 23.04C15.4489 21.9825 15.5389 20.1825 15.1114 18.1125C15.4939 18.0225 15.8764 18 16.2589 18C17.0464 18 17.2714 18.3375 17.3614 18.8775C17.5189 19.845 17.2939 21.96 15.0664 23.1975L14.9089 23.04Z" fill="#1F2B70"/>
                    <path d="M1.215 23.4675C0.6525 23.4675 0 23.1975 0 22.365V19.575C0 19.1025 0.3825 18.8325 0.6975 18.8325C0.72 19.3275 1.17 19.575 1.62 19.575H8.145C10.1025 19.575 11.025 20.835 11.025 22.6575V28.98L9.45 32.6925L1.53 32.6475C1.0575 31.5675 0.27 29.565 0.2925 27.36C0.315 25.6275 1.305 24.5475 2.565 23.4675H1.215ZM2.34 28.755H10.8675L10.71 25.92C10.62 24.255 9.8775 23.4675 8.2575 23.4675H2.8125C1.4175 24.66 1.4625 26.145 2.34 28.755Z" fill="#1F2B70"/>
                    <path d="M115.193 10.8004C113.895 10.8004 112.258 10.1232 112.258 8.03513V5.60816C112.258 4.42306 113.161 3.74586 113.895 3.74586C114.008 4.87452 115.136 5.60816 116.321 5.60816H120.159C122.472 5.60816 122.924 6.90612 122.924 8.03478V10.8004H119.538L109.719 38.8377H122.134C127.89 38.8377 128.68 37.3704 130.655 34.0408C133.928 28.5104 134.605 28.0589 134.605 25.5758H127.382C126.084 25.5758 124.447 24.8986 124.447 22.8106V15.8129C124.447 14.6278 125.35 13.9506 126.084 13.9506C126.197 15.0793 127.325 15.8129 128.51 15.8129H130.881C133.759 15.8129 135 16.998 135 19.4246V25.858C135 30.937 130.711 30.7677 130.711 42.2237C130.711 47.472 128.003 48.6006 123.206 48.6006H105.995V44.1988C105.995 41.0386 102.044 34.9438 102.044 31.7835C102.044 29.3569 103.229 26.5352 105.995 25.5758H102.891C101.48 25.5758 99.9 24.8986 99.9 22.8106V15.8129C99.9 14.6278 100.916 13.9506 101.593 13.9506C101.649 15.0793 102.778 15.8129 103.963 15.8129H107.97C110.17 15.8129 110.735 17.1109 110.735 18.2395V25.5758H107.97C106.559 25.5758 103.455 26.4788 103.455 29.7519C103.455 33.533 108.478 35.621 109.211 38.7813L118.974 10.8004H115.193Z" fill="#1F2B70"/>
                    <path d="M124.313 1.33474C124.08 1.24031 123.824 1.21549 123.576 1.26344C123.329 1.31138 123.102 1.42993 122.924 1.60397L121.275 3.20821L118.351 0.363263C118.112 0.130666 117.788 0 117.45 0C117.112 0 116.788 0.130666 116.549 0.363263L113.625 3.20821L111.976 1.60397C111.798 1.43051 111.571 1.31239 111.324 1.26453C111.076 1.21668 110.82 1.24125 110.587 1.33514C110.354 1.42902 110.155 1.58801 110.015 1.79199C109.875 1.99598 109.8 2.2358 109.8 2.48115V9.04823C109.801 9.87057 110.137 10.6589 110.735 11.2404C111.332 11.8219 112.142 12.149 112.987 12.15H121.912C122.758 12.149 123.568 11.8219 124.165 11.2404C124.763 10.6589 125.099 9.87057 125.1 9.04823V2.48115C125.1 2.23578 125.025 1.9959 124.885 1.79185C124.745 1.58779 124.546 1.42871 124.313 1.33474Z" fill="#1F2B70"/>
                </svg>
            </div>

            <div className="siumim-manager-form">
                <div className="form-headers">
                    <h1>הסיומים שלי</h1>
                    {activeSiumId && currentSium ? (
                        <h3>מציג את כל הסיומים של {currentSium.organizerName || currentSium.kehilaName}</h3>
                    ) : (
                        <h3>הזן אימייל וטלפון כדי לראות את כל הסיומים שלך</h3>
                    )}
                </div>

                <form onSubmit={handleSearch} className="search-form">
                    <div className="input-row">
                        <div className="input-container">
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="input"
                                placeholder="אימייל"
                                disabled={!!activeSiumId && !siumNotFound}
                            />
                        </div>
                        <div className="input-container">
                            <input
                                type="tel"
                                value={phone}
                                onChange={handlePhoneChange}
                                className="input"
                                placeholder="טלפון"
                                disabled={!!activeSiumId && !siumNotFound}
                            />
                        </div>
                    </div>

                    {errorMessage && <div className="fields-error-message center-text">{errorMessage}</div>}

                    {/* Only show search button when no siumId is provided or sium not found */}
                    {(!activeSiumId || siumNotFound) && siumim.length < 1 && (
                        <div className="input-container full-width">
                            {!isLoading ? (
                                <button type="submit" className="btn new-sium-button dark-button search-button">
                                    חיפוש הסיומים שלי
                                </button>
                            ) : (
                                <button type="button" className="btn new-sium-button dark-button search-button" disabled>
                                    <span className="spinner-border spinner-border-sm"></span>
                                </button>
                            )}
                        </div>
                    )}
                </form>

                {isSearched && siumim.length > 0 && (
                    <div className="siumim-list">
                        <div className="search-form">
                            <button
                                className="btn new-sium-button"
                                onClick={() => setShowNewSiumModal(true)}
                            >
                                יצירת סיום חדש
                            </button>
                        </div>
                        <h2 className="siumim-list-title">נמצאו {siumim.length} סיומים</h2>
                        <div className="siumim-cards">
                            {siumim.map((sium) => {
                                const status = getSiumStatus(sium);
                                const isCurrentSium = activeSiumId && sium.id === activeSiumId;
                                const backgroundColor = getSiumBackgroundColor(sium, siumim);

                                return (
                                    <div
                                        key={sium.id}
                                        className={`sium-card ${isCurrentSium ? 'current-sium' : ''}`}
                                        style={{ backgroundColor: backgroundColor }}
                                        onClick={() => navigateToSium(sium)}
                                    >
                                        {isCurrentSium && <div className="current-sium-badge">הסיום הנוכחי</div>}


                                        <div className="sium-card-header">
                                            <h3 className="sium-card-title">{sium.kehilaName}</h3>
                                            <span
                                                className="sium-card-status"
                                                style={{ backgroundColor: status.color }}
                                            >
                                                {status.text}
                                            </span>
                                        </div>

                                        <div className="sium-card-body">
                                            <div className="sium-card-info">
                                                <div className="sium-info-item">
                                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/>
                                                        <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
                                                    </svg>
                                                    <span style={{ fontWeight: 'bold' }}>{getSiumTypeLabel(sium)}</span>
                                                </div>

                                                <div className="sium-info-item">
                                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                                                        <circle cx="12" cy="7" r="4"/>
                                                    </svg>
                                                    <span>{sium.organizerName || '-'}</span>
                                                </div>

                                                <div className="sium-info-item">
                                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                                                        <line x1="16" y1="2" x2="16" y2="6"/>
                                                        <line x1="8" y1="2" x2="8" y2="6"/>
                                                        <line x1="3" y1="10" x2="21" y2="10"/>
                                                    </svg>
                                                    <span>{formatDate(sium.startSium)} - {formatDate(sium.endSium)}</span>
                                                </div>

                                                {sium.nishmat && (
                                                    <div className="sium-info-item">
                                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                                                        </svg>
                                                        <span className="nishmat-text">{sium.nishmat}</span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {/* Mini progress display */}
                                        {progressData[sium.id] && (
                                            <div className="sium-card-progress">
                                                <div className="sium-card-progress-item">
                                                    <span className="sium-card-progress-label">נתפסו</span>
                                                    <div className="sium-card-progress-bar-wrapper">
                                                        <div className="sium-card-progress-bar taken" style={{ width: `${progressData[sium.id].takenPercent}%` }}></div>
                                                    </div>
                                                    <span className="sium-card-progress-value">{progressData[sium.id].takenPercent}%</span>
                                                </div>
                                                <div className="sium-card-progress-item">
                                                    <span className="sium-card-progress-label">נלמדו</span>
                                                    <div className="sium-card-progress-bar-wrapper">
                                                        <div className="sium-card-progress-bar learned" style={{ width: `${progressData[sium.id].learnedPercent}%` }}></div>
                                                    </div>
                                                    <span className="sium-card-progress-value">{progressData[sium.id].learnedPercent}%</span>
                                                </div>
                                            </div>
                                        )}

                                        <div className="sium-card-footer">
                                            <span className="enter-sium-text">כניסה לסיום →</span>

                                            {/* Color picker button */}
                                            <div className="sium-card-color-picker">
                                                <button
                                                    className="color-picker-btn footer-color-btn"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setShowColorPicker(showColorPicker === sium.id ? null : sium.id);
                                                    }}
                                                    title="שינוי צבע רקע"
                                                >
                                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <circle cx="13.5" cy="6.5" r=".5"/>
                                                        <circle cx="17.5" cy="10.5" r=".5"/>
                                                        <circle cx="8.5" cy="7.5" r=".5"/>
                                                        <circle cx="6.5" cy="12.5" r=".5"/>
                                                        <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.926 0 1.648-.746 1.648-1.688 0-.437-.18-.835-.437-1.125-.29-.289-.438-.652-.438-1.125a1.64 1.64 0 0 1 1.668-1.668h1.996c3.051 0 5.555-2.503 5.555-5.554C21.965 6.012 17.461 2 12 2z"/>
                                                    </svg>
                                                </button>

                                                {showColorPicker === sium.id && (
                                                    <div className="color-picker-dropdown footer-dropdown" onClick={(e) => e.stopPropagation()}>
                                                        <div className="color-picker-title">בחר צבע רקע</div>
                                                        <div className="color-picker-colors">
                                                            {availableColors.map((color) => (
                                                                <button
                                                                    key={color}
                                                                    className={`color-option ${backgroundColor === color ? 'selected' : ''}`}
                                                                    style={{ backgroundColor: color }}
                                                                    onClick={() => handleColorChange(sium, color)}
                                                                    title={color}
                                                                >
                                                                    {backgroundColor === color && (
                                                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#333" strokeWidth="3">
                                                                            <polyline points="20 6 9 17 4 12"/>
                                                                        </svg>
                                                                    )}
                                                                </button>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>

                                            {isSiumEnded(sium) && (
                                                <button
                                                    className="btn btn-sm btn-outline-danger sium-delete-btn"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setSiumToDelete(sium);
                                                        setShowDeleteModal(true);
                                                    }}
                                                >
                                                    מחק
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {isSearched && siumim.length === 0 && !isLoading && (
                    <div className="no-siumim-found">
                        <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#414A87" strokeWidth="1">
                            <circle cx="11" cy="11" r="8"/>
                            <path d="m21 21-4.35-4.35"/>
                        </svg>
                        <p>לא נמצאו סיומים</p>
                        <button
                            className="btn new-sium-button"
                            onClick={() => setShowNewSiumModal(true)}
                        >
                            יצירת סיום חדש
                        </button>
                    </div>
                )}
            </div>

            <div className="sium-bottom-page">
                <a className="margin-left-10 no-link" href="/" onClick={(e) => { e.preventDefault(); navigate('/'); }}>חזרה לדף הבית</a>
                <span className="margin-left-10">|</span>
                <a className="margin-left-10 no-link" target="_blank" rel="noopener noreferrer" href="https://wa.me/9720509552993">צור איתנו קשר</a>
                <span className="margin-left-10">|</span>
                <span> © כל הזכויות שמורות לאתר סיום הש"ס</span>
            </div>

            {/* New Sium Options Modal */}
            {showNewSiumModal && (
                <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">בחר את הסיום שלך</h5>
                                <button type="button" className="btn-close" onClick={() => setShowNewSiumModal(false)}></button>
                            </div>
                                    <div className="modal-body new-sium-options-modal">
                                        {/* Main option - Shas Gemara */}
                                        <button
                                            className="sium-option-btn main-option"
                                            onClick={() => navigate('/newSium')}
                                        >
                                            <div className="sium-option-icon">
                                                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/>
                                                    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
                                                </svg>
                                            </div>
                                            <span className="sium-option-text">סיום ש"ס גמרא</span>
                                        </button>

                                        {/* Secondary options */}
                                        <div className="sium-options-secondary">
                                            <button
                                                className="sium-option-btn secondary-option"
                                                onClick={() => navigate('/newSiumAher?mipuiId=2rN4gXL26hGI6aGhuTPe')}
                                            >
                                                <div className="sium-option-icon-small">
                                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/>
                                                        <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
                                                    </svg>
                                                </div>
                                                <span className="sium-option-text-small">משנה תורה לרמב"ם</span>
                                            </button>

                                            <button
                                                className="sium-option-btn secondary-option"
                                                onClick={() => navigate('/newSiumAher?mipuiId=2RaGfiFR3lrsjWaqkRCQ')}
                                            >
                                                <div className="sium-option-icon-small">
                                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/>
                                                        <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
                                                    </svg>
                                                </div>
                                                <span className="sium-option-text-small">משנה ברורה</span>
                                            </button>

                                            <button
                                                className="sium-option-btn secondary-option"
                                                onClick={() => navigate('/newSiumAher?mipuiId=8yrjgmKjzFQ9Q2z0Qlpi')}
                                            >
                                                <div className="sium-option-icon-small">
                                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/>
                                                        <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
                                                    </svg>
                                                </div>
                                                <span className="sium-option-text-small">נביאים כתובים</span>
                                            </button>

                                            <button
                                                className="sium-option-btn secondary-option"
                                                onClick={() => navigate('/newSiumAher?mipuiId=9tS5EnuUNpVJSacnigBB')}
                                            >
                                                <div className="sium-option-icon-small">
                                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/>
                                                        <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
                                                    </svg>
                                                </div>
                                                <span className="sium-option-text-small">שולחן ערוך</span>
                                            </button>

                                            <button
                                                className="sium-option-btn secondary-option"
                                                onClick={() => navigate('/newSiumAher?mipuiId=CefBqHOX8jgds4shamBN')}
                                            >
                                                <div className="sium-option-icon-small">
                                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/>
                                                        <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
                                                    </svg>
                                                </div>
                                                <span className="sium-option-text-small">ש"ס משניות</span>
                                            </button>

                                            <button
                                                className="sium-option-btn secondary-option"
                                                onClick={() => navigate('/newSiumAher?mipuiId=qaG7gfXyuoxWhOm3EXWq')}
                                            >
                                                <div className="sium-option-icon-small">
                                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/>
                                                        <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
                                                    </svg>
                                                </div>
                                                <span className="sium-option-text-small">רמב"ן על התורה</span>
                                            </button>

                                            <button
                                                className="sium-option-btn secondary-option"
                                                onClick={() => navigate('/newSiumAher?mipuiId=aFUAaa6dMyoVRxyY6RmF')}
                                            >
                                                <div className="sium-option-icon-small">
                                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/>
                                                        <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
                                                    </svg>
                                                </div>
                                                <span className="sium-option-text-small">ספר תהילים</span>
                                            </button>

                                            <button
                                                className="sium-option-btn secondary-option"
                                                onClick={() => navigate('/newSiumAher?mipuiId=h7ijjKWyvikU6Kf7MIun')}
                                            >
                                                <div className="sium-option-icon-small">
                                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/>
                                                        <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
                                                    </svg>
                                                </div>
                                                <span className="sium-option-text-small">ליקוטי מוהר"ן</span>
                                            </button>

                                            <button
                                                className="sium-option-btn secondary-option"
                                                onClick={() => navigate('/newSiumAher?mipuiId=aCdIT9ZJiGekYfsi7RSh')}
                                            >
                                                <div className="sium-option-icon-small">
                                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/>
                                                        <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
                                                    </svg>
                                                </div>
                                                <span className="sium-option-text-small">ספר התניא</span>
                                            </button>
                                        </div>

                                        {/* Additional main option - Other */}
                                        <button
                                            className="sium-option-btn main-option"
                                            onClick={() => navigate('/newSiumAher')}
                                        >
                                            <div className="sium-option-icon">
                                                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/>
                                                    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
                                                </svg>
                                            </div>
                                            <span className="sium-option-text">עוד</span>
                                        </button>
                                    </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {showDeleteModal && siumToDelete && (
                <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">מחיקת סיום</h5>
                                <button type="button" className="btn-close" onClick={() => {
                                    setShowDeleteModal(false);
                                    setSiumToDelete(null);
                                }}></button>
                            </div>
                            <div className="modal-body">
                                <p>האם אתה בטוח שברצונך למחוק את הסיום <strong>"{siumToDelete.kehilaName}"</strong>?</p>
                                <p className="text-muted">הסיום יוסר מהרשימה אך הנתונים יישמרו במערכת.</p>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => {
                                    setShowDeleteModal(false);
                                    setSiumToDelete(null);
                                }}>ביטול</button>
                                <button type="button" className="btn btn-danger" onClick={handleDeleteSium}>מחק</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SiumimManager;
